import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useContext,
  useRef,
  useMemo,
} from 'react';
import {
  getAllRegisteredStores,
  getAllPendingOperations,
} from '../utils/offlineStoreRegistry';
import { hasPendingOperations } from '../utils/offlineStoreRegistry';
import { createCoalescedTask } from '../utils/coalescedTask';
import { useOfflineModeContext } from './offlineContext';
import { intervalCoordinator } from '../utils/intervalCoordinator';
import {
  getSyncService,
  isSyncServiceInitialized,
} from '../services/offlineSyncService';
import { recoverOrphanedSyncBackups } from '../utils/offlineSyncRecovery';
import { removeQueuedFieldPhotoOperations } from '../utils/fieldPhotos';
import localforage from 'localforage';
import { notificationPending, syncNotificationsManually } from '../notifications/bridge';

const OfflineStatusContext = createContext({
  totalPendingCount: 0,
  storesWithOps: [],
  storeNames: {},
  loading: true,
  refreshStatus: async (showLoading = false) => {},
  processingStores: {},
  isStoreProcessing: (storeName) => false,
  markStoreAsProcessing: (storeName) => {},
  markStoreAsDone: (storeName) => {},
  connectionQuality: 'unknown',
  networkChangeHistory: [],
  autoSyncEnabled: true,
  setAutoSyncEnabled: (enabled) => {},
  syncAllStoresManually: async (onProgress) => {},
  orphanedStores: [],
  restoreOrphanedOps: async (storeName) => {},
  discardOrphanedOps: async (storeName) => {},
});

export const OfflineStatusProvider = ({
  children,
  refreshInterval = 60000,
}) => {
  const [totalPendingCount, setTotalPendingCount] = useState(0);
  const [storesWithOps, setStoresWithOps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingStores, setProcessingStores] = useState({});
  const [connectionQuality, setConnectionQuality] = useState('unknown');
  const [networkChangeHistory, setNetworkChangeHistory] = useState([]);
  const [orphanedStores, setOrphanedStores] = useState([]);
  const [autoSyncEnabled, setAutoSyncEnabledState] = useState(() => {
    // Load from localStorage, default to false so users start in manual mode
    const saved = localStorage.getItem('msales_autoSyncEnabled');
    return saved !== null ? JSON.parse(saved) : false;
  });
  const { networkState } = useOfflineModeContext();
  const intervalRef = useRef(null);
  const prevOnlineStateRef = useRef(networkState.online);
  const prevConnectionTypeRef = useRef(networkState?.effectiveType);

  const storeNames = useMemo(
    () => ({
      visits: 'Posjete',
      field_photos: 'Fotografije objekata',
      orders: 'Narudžbe',
      surveys: 'Ankete',
      tasks_comments: 'Komentari',
      tasks_documents: 'Dokumenti',
      tasks_status: 'Status zadataka',
      tasks_execution: 'Izvršenje zadataka',
      tasks_distribution: 'Distribucija',
      distribution_execution: 'Izvršenje distribucije',
      Tasks: 'Zadaci',
      Reports: 'Izvještaji',
      Customers: 'Kupci',
      Products: 'Artikli',
      Comments: 'Komentari Zadataka',
      OpenItems: 'Otvorene stavke',
      notifications: 'Obavijesti',
    }),
    [],
  );

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    recoverOrphanedSyncBackups()
      .then(setOrphanedStores)
      .catch(console.error);
  }, []);

  // Wrapper for setAutoSyncEnabled that persists to localStorage
  const setAutoSyncEnabled = useCallback(async (enabled) => {
    if (enabled) {
      try {
        if (await hasPendingOperations()) return false;
      } catch (error) {
        console.error(
          '[OfflineStatus] Could not verify pending operations:',
          error,
        );
        return false;
      }
    }

    setAutoSyncEnabledState(enabled);
    localStorage.setItem('msales_autoSyncEnabled', JSON.stringify(enabled));
    console.log(
      `[OfflineStatus] Auto-sync ${enabled ? 'enabled' : 'disabled'}`,
    );
    return true;
  }, []);

  const readStatus = useCallback(async () => {
    if (!isMountedRef.current) return;
    try {
      const stores = await getAllRegisteredStores();
      const allOpsMap = await getAllPendingOperations(stores);
      const totalCount = Object.values(allOpsMap).reduce((total, operations) => total + operations.length, 0);

      const combinedData = stores.map((storeInfo) => {
        const operations = allOpsMap[storeInfo.storeName] || [];
        return {
          ...storeInfo,
          operations: operations,
          count: operations.length,
        };
      });
      const notificationOps = await notificationPending();
      if (!isMountedRef.current) return;
      if (notificationOps.length) combinedData.push({ storeName: 'notifications', redirectLink: '/notifications', operations: notificationOps, count: notificationOps.length });

      setStoresWithOps((prevData) => {
        try {
          if (!Array.isArray(prevData) || !Array.isArray(combinedData)) {
            return combinedData;
          }
          if (prevData.length !== combinedData.length) return combinedData;
          for (let i = 0; i < combinedData.length; i++) {
            const prev = prevData[i];
            const next = combinedData[i];
            if (
              !prev ||
              !next ||
              prev.storeName !== next.storeName ||
              prev.count !== next.count
            ) {
              return combinedData;
            }
            const prevOps = (prev && prev.operations) || [];
            const nextOps = (next && next.operations) || [];
            for (let j = 0; j < nextOps.length; j++) {
              const prevId = prevOps[j] && prevOps[j].id;
              const nextId = nextOps[j] && nextOps[j].id;
              const prevReview = prevOps[j] && prevOps[j].needsPricingReview;
              const nextReview = nextOps[j] && nextOps[j].needsPricingReview;
              if (prevId !== nextId || prevReview !== nextReview) {
                return combinedData;
              }
            }
          }
          return prevData;
        } catch (e) {
          console.warn(
            '[OfflineStatusContext] compare failed, falling back:',
            e && e.message,
          );
          return combinedData;
        }
      });
      setTotalPendingCount((prevCount) => {
        const hasChanged = prevCount !== totalCount + notificationOps.length;
        if (hasChanged) {
          return totalCount + notificationOps.length;
        }
        return prevCount;
      });
    } catch (error) {
      console.error(
        '[OfflineStatusContext] Error refreshing offline status:',
        error,
      );
    }
  }, []);

  const refreshTask = useRef(null);
  if (!refreshTask.current) refreshTask.current = createCoalescedTask(readStatus);
  const refreshStatus = useCallback(async (showLoading = false) => {
    if (!isMountedRef.current) return;
    if (showLoading) setLoading(true);
    try {
      await refreshTask.current();
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, []);

  const restoreOrphanedOps = useCallback(async (storeName) => {
    try {
      const operationStore = localforage.createInstance({
        name: `OfflineOperations_${storeName}`,
      });
      const orphaned = (await operationStore.getItem('orphaned_ops')) || [];
      if (orphaned.length === 0) return;
      const current = (await operationStore.getItem('operations')) || [];
      const currentIds = new Set(current.map((op) => op.id));
      const toRestore = orphaned.filter((op) => !currentIds.has(op.id));
      await operationStore.setItem('operations', [...current, ...toRestore]);
      await operationStore.removeItem('orphaned_ops');
      setOrphanedStores((prev) => prev.filter((s) => s.storeName !== storeName));
      await refreshStatus(false);
    } catch (err) {
      console.error(`[OfflineStatus] Failed to restore orphaned ops for "${storeName}":`, err);
    }
  }, [refreshStatus]);

  const discardOrphanedOps = useCallback(async (storeName) => {
    try {
      const operationStore = localforage.createInstance({
        name: `OfflineOperations_${storeName}`,
      });
      await operationStore.removeItem('orphaned_ops');
      setOrphanedStores((prev) => prev.filter((s) => s.storeName !== storeName));
    } catch (err) {
      console.error(`[OfflineStatus] Failed to discard orphaned ops for "${storeName}":`, err);
    }
  }, []);

  useEffect(() => {
    refreshStatus(true);

    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Register coordinated offline status refresh
    if (refreshInterval > 0) {
      console.log(
        `Setting up offline status refresh interval: ${refreshInterval}ms`,
      );

      intervalCoordinator.registerInterval(
        'OfflineStatus_refresh',
        () => {
          if (networkState.online && isMountedRef.current) {
            refreshStatus(false);
          }
        },
        refreshInterval,
        {
          priority: 'DATA_SYNC',
          maxExecutionTime: 30,
          deviceScaling: true,
        },
      );
    }

    return () => {
      intervalCoordinator.clearInterval('OfflineStatus_refresh');
      if (intervalRef.current) {
        console.log('Clearing offline status refresh interval.');
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [refreshInterval, networkState.online]);

  useEffect(() => {
    const wasOffline = !prevOnlineStateRef.current;
    const isOnlineNow = networkState.online;
    if (wasOffline && isOnlineNow && !loading) {
      console.log('Network transitioned to ONLINE, triggering status refresh.');
      refreshStatus(true);
    }
    prevOnlineStateRef.current = networkState.online;
  }, [networkState.online, loading, refreshStatus]);

  // Enhanced network quality monitoring
  useEffect(() => {
    const currentConnectionType = networkState?.effectiveType || 'unknown';
    const isOnline = networkState?.online;
    const previousConnectionType = prevConnectionTypeRef.current;

    // Determine connection quality
    let quality = 'unknown';
    if (!isOnline) {
      quality = 'offline';
    } else if (currentConnectionType === 'slow-2g') {
      quality = 'poor';
    } else if (currentConnectionType === '2g') {
      quality = 'low';
    } else if (currentConnectionType === '3g') {
      quality = 'moderate';
    } else if (['4g', '5g'].includes(currentConnectionType)) {
      quality = 'good';
    }

    setConnectionQuality(quality);

    // Track significant connection changes
    if (
      previousConnectionType &&
      previousConnectionType !== currentConnectionType
    ) {
      const timestamp = new Date().toISOString();
      const change = {
        timestamp,
        from: previousConnectionType,
        to: currentConnectionType,
        qualityChange:
          previousConnectionType !== 'unknown'
            ? getQualityChange(previousConnectionType, currentConnectionType)
            : null,
      };

      console.log('Network connection change detected:', change);

      setNetworkChangeHistory((prev) => {
        const newHistory = [change, ...prev].slice(0, 10); // Keep last 10 changes
        return newHistory;
      });
    }

    prevConnectionTypeRef.current = currentConnectionType;
  }, [networkState?.effectiveType, networkState?.online]);

  // Helper function to determine quality change direction
  const getQualityChange = (from, to) => {
    const qualityOrder = ['slow-2g', '2g', '3g', '4g', '5g'];
    const fromIndex = qualityOrder.indexOf(from);
    const toIndex = qualityOrder.indexOf(to);

    if (fromIndex === -1 || toIndex === -1) return 'unknown';
    if (toIndex > fromIndex) return 'improved';
    if (toIndex < fromIndex) return 'degraded';
    return 'stable';
  };

  const isStoreProcessing = useCallback(
    (storeName) => {
      return !!processingStores[storeName];
    },
    [processingStores],
  );

  const markStoreAsProcessing = useCallback((storeName) => {
    if (!isMountedRef.current) return;
    setProcessingStores((prev) => ({ ...prev, [storeName]: true }));
  }, []);

  const markStoreAsDone = useCallback((storeName) => {
    if (!isMountedRef.current) return;
    setProcessingStores((prev) => {
      const newState = { ...prev };
      delete newState[storeName];
      return newState;
    });
  }, []);

  const syncAllStoresManually = useCallback(
    async (onProgress) => {
      if (!isSyncServiceInitialized()) {
        console.error('[OfflineStatus] SyncService not initialized');
        throw new Error('Sync service nije inicijaliziran');
      }

      const syncService = getSyncService();
      const storesToSync = storesWithOps.filter((store) => store.count > 0 && store.storeName !== 'notifications');
      try {
        const result = await syncNotificationsManually();
        onProgress && onProgress({ storeName: 'Obavijesti', ...result, status: result.failed ? 'partial' : 'success' });
      } catch (error) {
        onProgress && onProgress({ storeName: 'Obavijesti', ...error.notificationSummary, status: error.notificationSummary?.synced ? 'partial' : 'error', error: 'Obavijesti nisu sinhronizovane' });
      }

      for (const store of storesToSync) {
        const operationStore = localforage.createInstance({
          name: `OfflineOperations_${store.storeName}`,
        });
        let operations = [];

        try {
          onProgress &&
            onProgress({
              storeName: storeNames[store.storeName] || store.storeName,
              status: 'syncing',
              count: store.count,
            });

          markStoreAsProcessing(store.storeName);

          operations = (await operationStore.getItem('operations')) || [];

          if (operations.length > 0) {
            await operationStore.setItem('sync_backup', operations);
          }

          const results = await syncService.processPendingOperationsForStore(
            store.storeName,
            operations,
          );

          const successfulIds = results
            .filter((r) => r.success)
            .map((r) => r.operationId);

          const currentOperations = (await operationStore.getItem('operations')) || [];
          const remainingOps = currentOperations.filter(
            (op) => !successfulIds.includes(op.id),
          );

          if (store.storeName === 'field_photos') await removeQueuedFieldPhotoOperations(successfulIds);
          else await operationStore.setItem('operations', remainingOps);
          await operationStore.removeItem('sync_backup');

          markStoreAsDone(store.storeName);

          const failedCount = results.filter((result) =>
            !result.success && !result.pendingPricingReview).length;

          onProgress &&
            onProgress({
              storeName: storeNames[store.storeName] || store.storeName,
              status: failedCount > 0 || results.some((result) => result.pendingPricingReview)
                ? 'partial' : 'success',
              synced: successfulIds.length,
              failed: failedCount,
            });
        } catch (error) {
          console.error(
            `[OfflineStatus] Error syncing store ${store.storeName}:`,
            error,
          );
          markStoreAsDone(store.storeName);

          try {
            const backup = (await operationStore.getItem('sync_backup')) || [];
            if (backup.length > 0) {
              const currentOps = (await operationStore.getItem('operations')) || [];
              if (currentOps.length < backup.length) {
                await operationStore.setItem('operations', backup);
              }
              await operationStore.removeItem('sync_backup');
            }
          } catch {}

          onProgress &&
            onProgress({
              storeName: storeNames[store.storeName] || store.storeName,
              status: 'error',
              error: error.message,
            });
        }
      }

      await refreshStatus(false);
    },
    [
      storesWithOps,
      storeNames,
      refreshStatus,
      markStoreAsProcessing,
      markStoreAsDone,
    ],
  );

  const value = useMemo(
    () => ({
      totalPendingCount,
      storesWithOps,
      loading,
      storeNames,
      refreshStatus,
      processingStores,
      isStoreProcessing,
      markStoreAsProcessing,
      markStoreAsDone,
      connectionQuality,
      networkChangeHistory,
      autoSyncEnabled,
      setAutoSyncEnabled,
      syncAllStoresManually,
      orphanedStores,
      restoreOrphanedOps,
      discardOrphanedOps,
    }),
    [
      totalPendingCount,
      storesWithOps,
      loading,
      storeNames,
      refreshStatus,
      processingStores,
      isStoreProcessing,
      markStoreAsProcessing,
      markStoreAsDone,
      connectionQuality,
      networkChangeHistory,
      autoSyncEnabled,
      setAutoSyncEnabled,
      syncAllStoresManually,
      orphanedStores,
      restoreOrphanedOps,
      discardOrphanedOps,
    ],
  );

  return (
    <OfflineStatusContext.Provider value={value}>
      {children}
    </OfflineStatusContext.Provider>
  );
};

export const useOfflineStatusContext = () => {
  return useContext(OfflineStatusContext);
};
